import React, { useState, useCallback, useId } from 'react';
import { Box, Typography, Alert, Stack, Paper, CircularProgress } from '@mui/material';
import CustomIcons from '../../assests/CustomIcons';

export interface DragDropFileUploaderProps {
  multiple?: boolean;
  acceptedTypes?: string[];
  onFilesSelected?: (files: File[]) => void;
  onError?: (errors: string[]) => void;
  showPreview?: boolean;
  height?: number | string;
  borderColor?: string;
  bgColor?: string;
  maxSizeByType?: Record<string, number>;

  /* ✅ NEW CUSTOMIZATION PROPS */
  iconSize?: number;
  titleFontSize?: number;
  subTitleFontSize?: number;
  loading?: boolean;
  disabled?: boolean;
}

interface PreviewFile extends File {
  preview: string;
}

const DragDropFileUploader: React.FC<DragDropFileUploaderProps> = ({
  multiple = true,
  maxSizeByType,
  acceptedTypes = ['image/*', 'application/pdf'],
  onFilesSelected = () => { },
  onError = () => { },
  showPreview = true,
  height = 180,
  borderColor = '#1976d2',
  bgColor = '#ffff',

  /* ✅ DEFAULT VALUES */
  iconSize = 40,
  titleFontSize = 14,
  subTitleFontSize = 12,
  loading,
  disabled
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [previews, setPreviews] = useState<PreviewFile[]>([]);
  const inputId = useId();

  const isDIsabled = disabled || loading;

  const validateFiles = (files: FileList): File[] => {
    const errorList: string[] = [];
    const validFiles: File[] = [];

    Array.from(files).forEach(file => {
      // Extract file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      // Check if file type matches accepted types
      let matchedType = acceptedTypes.find(type => {
        const regex = new RegExp(`^${type.replace('*', '.*')}$`);
        return regex.test(file.type);
      });

      // Fallback: Check by file extension for HEIC/HEIF (some browsers don't report correct MIME type)
      if (!matchedType && (fileExtension === 'heic' || fileExtension === 'heif')) {
        // Check if image/heic or image/heif is in acceptedTypes
        matchedType = acceptedTypes.find(type => type === 'image/heic' || type === 'image/heif' || type === 'image/*');

        // If found, use the specific type for size validation
        if (matchedType === 'image/*') {
          matchedType = fileExtension === 'heic' ? 'image/heic' : 'image/heif';
        }
      }

      if (!matchedType) {
        errorList.push(`${file.name} has an invalid file type`);
        return;
      }

      const customMaxSize = maxSizeByType?.[matchedType];
      if (!customMaxSize) {
        errorList.push(`No size limit defined for type: ${matchedType}`);
        return;
      }

      const maxBytes = customMaxSize * 1024 * 1024;
      if (file.size > maxBytes) {
        errorList.push(`${file.name} exceeds ${customMaxSize} MB limit for ${matchedType}`);
        return;
      }

      validFiles.push(file);
    });

    setErrors(errorList);
    if (errorList.length) onError(errorList);

    return validFiles;
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (isDIsabled) return;
      setDragActive(false);

      const validated = validateFiles(e.dataTransfer.files);
      if (validated.length) {
        onFilesSelected(validated);
        if (showPreview) {
          const previews = validated.map(file =>
            Object.assign(file, {
              preview: URL.createObjectURL(file)
            })
          );
          setPreviews(prev => [...prev, ...(previews as PreviewFile[])]);
        }
      }
    },
    [acceptedTypes, maxSizeByType, showPreview, isDIsabled]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const validated = validateFiles(e.target.files);

    if (validated.length) {
      onFilesSelected(validated);
      if (showPreview) {
        const previews = validated.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        );
        setPreviews(prev => [...prev, ...(previews as PreviewFile[])]);
      }
    }

    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Errors */}
      {errors.length > 0 && (
        <Stack spacing={1} mb={2}>
          {errors.map((err, idx) => (
            <Alert severity="error" key={idx}>
              {err}
            </Alert>
          ))}
        </Stack>
      )}

      {/* Drop Zone */}
      <Paper
        elevation={0}
        sx={{
          border: `2px dashed ${dragActive ? borderColor : '#d8d8d8'}`,
          borderRadius: 4,
          height,
          cursor: isDIsabled ? 'not-allowed' : 'pointer',
          bgcolor: dragActive ? '#e9f3ff' : bgColor,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          textAlign: 'center',
          transition: '0.25s',
          '&:hover': {
            bgcolor: isDIsabled ? bgColor : '#f3f4f6'
          }
        }}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onDragEnter={() => !isDIsabled && setDragActive(true)}
        onDragLeave={() => setDragActive(false)}
        onClick={() => !isDIsabled && document.getElementById(inputId)?.click()}
      >
        {loading ? (
          <>
            <CircularProgress
              size={iconSize || 36}
              sx={{
                mb: 1,
                '& svg': {
                  width: '100% !important',
                  height: '100% !important'
                }
              }}
            />
            <Typography
              sx={{
                fontSize: titleFontSize,
                fontWeight: 500
              }}
            >
              Uploading media...
            </Typography>
          </>
        ) : (
          <>
            {/* ✅ ICON SIZE CONTROL */}
            <CustomIcons.MediaUpload
              style={{
                width: iconSize,
                height: iconSize,
                marginBottom: 8,
                opacity: disabled ? 0.5 : 1
              }}
            />

            {/* ✅ FONT SIZE CONTROL */}
            <Typography sx={{ fontSize: titleFontSize, fontWeight: 400, opacity: disabled ? 0.5 : 1 }}>
              Drag & Drop your files
            </Typography>

            <Typography
              sx={{
                fontSize: subTitleFontSize,
                color: 'text.secondary',
                mt: 0.5,
                opacity: disabled ? 0.5 : 1
              }}
            >
              or click to browse from device
            </Typography>
          </>
        )}
      </Paper>

      {/* Hidden Input */}
      <input
        id={inputId}
        type="file"
        multiple={multiple}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={isDIsabled}
        accept={[
          ...acceptedTypes,
          // Add file extensions for better Windows compatibility
          ...(acceptedTypes.includes('image/heic') || acceptedTypes.includes('image/heif') ? ['.heic', '.heif'] : []),
          ...(acceptedTypes.includes('image/jpeg') ? ['.jpg', '.jpeg'] : []),
          ...(acceptedTypes.includes('image/png') ? ['.png'] : []),
          ...(acceptedTypes.includes('image/webp') ? ['.webp'] : []),
          ...(acceptedTypes.includes('video/mp4') ? ['.mp4'] : [])
        ].join(',')}
      />

      {/* Preview */}
      {showPreview && previews.length > 0 && (
        <Stack direction="row" spacing={2} mt={3} flexWrap="wrap">
          {previews.map((file, idx) => (
            <Paper
              key={idx}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 3,
                overflow: 'hidden',
                p: 1
              }}
            >
              <img
                src={file.preview}
                alt="preview"
                style={{
                  width: '100%',
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 6
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {file.name}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default DragDropFileUploader;
